<template>
  <div class="prompt-editor">
    <v-row no-gutters style="min-height: 480px;">

      <!-- Left: version history -->
      <v-col cols="12" md="3" class="prompt-editor__history">
        <div class="prompt-editor__history-title overline px-3 pt-2 pb-1">
          Historique
        </div>
        <v-divider />
        <div v-if="!versions.length" class="caption grey--text px-3 py-2">
          Aucune version
        </div>
        <v-list dense nav v-else class="pa-0">
          <v-list-item
            v-for="v in versions"
            :key="v.version"
            :input-value="selectedVersion && selectedVersion.version === v.version"
            color="primary"
            @click="selectVersion(v)"
          >
            <v-list-item-content>
              <v-list-item-title class="caption font-weight-medium d-flex align-center">
                v{{ v.version }}
                <v-chip v-if="v.version === activeVersion" x-small color="success" class="ml-2" style="height: 16px">
                  actif
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ formatDate(v.created_at) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-col>

      <v-divider vertical />

      <!-- Right: edit / preview tabs -->
      <v-col cols="12" md="9" class="d-flex flex-column">
        <v-tabs v-model="tab" height="36">
          <v-tab>
            <v-icon left x-small>mdi-pencil-outline</v-icon>
            Édition
          </v-tab>
          <v-tab>
            <v-icon left x-small>mdi-eye-outline</v-icon>
            Aperçu
            <v-chip v-if="selectedVersion" x-small class="ml-2" color="primary" outlined>
              v{{ selectedVersion.version }}
            </v-chip>
          </v-tab>
          <v-spacer />
          <slot name="actions" />
        </v-tabs>
        <v-divider />

        <v-tabs-items v-model="tab" class="flex-grow-1">

          <!-- Edit tab -->
          <v-tab-item class="fill-height">
            <v-textarea
              :value="value"
              :disabled="disabled"
              outlined
              auto-grow
              rows="16"
              hide-details
              class="prompt-editor__textarea ma-3"
              @input="$emit('input', $event)"
            />
          </v-tab-item>

          <!-- Preview tab -->
          <v-tab-item class="fill-height">
            <div class="prompt-editor__preview ma-3">
              <div
                v-if="previewText"
                class="markdown-body"
                v-html="rendered"
              />
              <span v-else class="prompt-editor__placeholder">Aucun contenu</span>
            </div>
            <div v-if="selectedVersion && selectedVersion.version !== activeVersion" class="d-flex justify-end pa-3 pt-0">
              <v-btn small text color="primary" @click="restoreVersion">
                <v-icon left small>mdi-restore</v-icon>
                Restaurer cette version
              </v-btn>
            </div>
          </v-tab-item>

        </v-tabs-items>
      </v-col>

    </v-row>

    <div v-if="hint" class="v-messages theme--light mt-2 pl-1">
      <div class="v-messages__wrapper">
        <div class="v-messages__message">{{ hint }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'

const md = new MarkdownIt({ breaks: true, linkify: true })

export default {
  name: 'PromptEditor',

  props: {
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    versions: { type: Array, default: () => [] },
    activeVersion: { type: Number, default: null },
  },

  data() {
    return {
      tab: 0,
      selectedVersion: null,
    }
  },

  computed: {
    previewText() {
      return this.selectedVersion ? this.selectedVersion.text : this.value
    },

    rendered() {
      return md.render(this.previewText || '')
    },
  },

  methods: {
    selectVersion(v) {
      if (this.selectedVersion && this.selectedVersion.version === v.version) {
        this.selectedVersion = null
      } else {
        this.selectedVersion = v
        this.tab = 1
      }
    },

    restoreVersion() {
      this.$emit('input', this.selectedVersion.text)
      this.selectedVersion = null
      this.tab = 0
    },

    formatDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.prompt-editor {
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  overflow: hidden;
}

.prompt-editor__history {
  background: rgba(128, 128, 128, 0.05);
  overflow-y: auto;
  max-height: 520px;
}

.prompt-editor__history-title {
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.7rem;
}

.prompt-editor__textarea {
  font-family: monospace;
  font-size: 0.85rem;
}

.prompt-editor__textarea :deep(.v-input__slot) {
  border: none !important;
  box-shadow: none !important;
}

.prompt-editor__textarea :deep(fieldset) {
  border: none !important;
}

.prompt-editor__preview {
  min-height: 200px;
  font-size: 0.875rem;
  line-height: 1.6;
}

.prompt-editor__placeholder {
  color: rgba(0, 0, 0, 0.38);
  font-style: italic;
}

/* Markdown typography */
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3) {
  margin-top: 0.75em;
  margin-bottom: 0.25em;
  font-weight: 600;
  line-height: 1.3;
}
.markdown-body :deep(h1) { font-size: 1.25em; }
.markdown-body :deep(h2) { font-size: 1.1em; }
.markdown-body :deep(h3) { font-size: 1em; }

.markdown-body :deep(p) { margin-top: 0; margin-bottom: 0.6em; }

.markdown-body :deep(ul),
.markdown-body :deep(ol) { padding-left: 1.4em; margin-bottom: 0.6em; }

.markdown-body :deep(li) { margin-bottom: 0.2em; }

.markdown-body :deep(code) {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 3px;
  padding: 0.1em 0.35em;
  font-size: 0.85em;
  font-family: monospace;
}

.markdown-body :deep(pre) {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: 0.75em 1em;
  overflow-x: auto;
  margin-bottom: 0.6em;
}

.markdown-body :deep(pre code) { background: none; padding: 0; }

.markdown-body :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.15);
  padding-left: 0.75em;
  margin: 0 0 0.6em 0;
  color: rgba(0, 0, 0, 0.6);
}

.markdown-body :deep(hr) {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 0.75em 0;
}

.markdown-body :deep(a) { color: #1976d2; }

.markdown-body > :deep(:last-child) { margin-bottom: 0; }
</style>
